import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../../../lib/api';
import { toast } from 'sonner';
import { Card } from '../../../components/ui/card';
import { Loader2, Layers, ChevronRight, Info } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../../../components/ui/popover';
import DemoPeriodSelector from './DemoPeriodSelector';
import { PageHeader, Stat } from './DemoUI';

const inr = (n) => (n === null || n === undefined ? '—' : `₹${Number(n).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`);
const pct = (n) => (n === null || n === undefined ? '—' : `${n}%`);
const TONE = ['border-l-violet-500', 'border-l-emerald-500', 'border-l-sky-500', 'border-l-amber-500', 'border-l-rose-500', 'border-l-slate-400'];

/** Demos grouped by the technology bucket of the course — trainers or counsellors inside. */
export default function DemoCategoryTab({ embedded = false, fixedPeriod = null }) {
  const [period, setPeriod] = useState(null);
  const [by, setBy] = useState('trainer');
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [shut, setShut] = useState({});

  const win = fixedPeriod || (period ? { date_from: period.from_date, date_to: period.to_date } : null);
  const key = JSON.stringify({ win, by });

  const load = useCallback(async () => {
    if (!win) return;
    setLoading(true);
    try {
      const r = await api.get('/training/demos/reports/by-category', { params: { ...win, by } });
      setD(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not load the technology view'); }
    finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  useEffect(() => { load(); }, [load]);

  const groups = d?.groups || [];
  const g0 = d?.grand || {};
  const share = useMemo(() => (v) => (g0.demos ? Math.round((v / g0.demos) * 100) : 0), [g0]);

  return (
    <div className={embedded ? 'space-y-5' : 'space-y-8'} data-testid="demo-category-tab">
      {!embedded && (
        <PageHeader icon={Layers} eyebrow="Trainer Demo" title="Demos by Technology"
          subtitle="Grouped by the Zoho Books category behind each course." testid="demo-category-header" />
      )}

      <Card className="p-3.5 border bg-slate-50/70 flex flex-wrap items-center gap-3">
        {!fixedPeriod && <DemoPeriodSelector onChange={setPeriod} />}
        <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-lg p-0.5 ml-auto" data-testid="demo-cat-by">
          {[['trainer', 'By Trainer'], ['counsellor', 'By Counsellor']].map(([k, l]) => (
            <button key={k} onClick={() => setBy(k)}
              className={`px-2.5 py-1.5 text-[11.5px] font-semibold rounded-md transition-colors ${
                by === k ? 'bg-violet-600 text-white' : 'text-slate-600 hover:bg-slate-50'}`}
              data-testid={`demo-cat-by-${k}`}>{l}</button>
          ))}
        </div>
        <Popover>
          <PopoverTrigger asChild>
            <button className="text-slate-400 hover:text-violet-700" data-testid="demo-cat-info"><Info className="w-4 h-4" /></button>
          </PopoverTrigger>
          <PopoverContent className="w-80 bg-white text-[11.5px] text-slate-600" align="end">
            <p className="font-semibold text-slate-800 mb-1 text-[12.5px]">Where the technology comes from</p>
            <p className="mb-2">Each course inherits the Zoho Books category billed to the students in its batches — the same
              field the Revenue Report groups by. Nothing is tagged by hand.</p>
            <p>Non-technology categories are left out{d?.hidden_categories?.length ? `: ${d.hidden_categories.join(', ')}` : ''}.
              A course nobody has been billed for yet shows under <b>Unmapped</b> until its first invoice.</p>
          </PopoverContent>
        </Popover>
      </Card>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <Stat label="Demos conducted" value={g0.demos ?? 0} icon={Layers} tone="violet" />
        <Stat label="Admissions" value={g0.admissions ?? 0} icon={Layers} tone="emerald" />
        <Stat label="Conversion" value={pct(g0.conversion_pct)} icon={Layers} tone="sky" />
        <Stat label="Not conducted" value={g0.not_conducted ?? 0} icon={Layers} tone="amber" />
        <Stat label="Admission value" value={inr(g0.value_ex_gst)} sub="excl. GST" icon={Layers} tone="emerald" />
      </div>

      {loading && (
        <Card className="border p-12 text-center text-[12px] text-slate-400" data-testid="demo-cat-loading">
          <Loader2 className="w-4 h-4 animate-spin inline mr-2" />Grouping the demos by technology…
        </Card>
      )}
      {!loading && groups.length === 0 && (
        <Card className="border p-12 text-center text-[12px] text-slate-400" data-testid="demo-cat-empty">No demos in this period.</Card>
      )}

      {!loading && groups.map((g, gi) => {
        const open = !shut[g.category];
        const t = g.totals;
        return (
          <Card key={g.category} className={`border border-l-4 ${TONE[gi % TONE.length]} overflow-hidden`} data-testid={`demo-cat-${g.category}`}>
            <button onClick={() => setShut((p) => ({ ...p, [g.category]: open }))}
              className="w-full flex flex-wrap items-center gap-x-5 gap-y-2 px-4 py-3 hover:bg-slate-50/80 transition-colors text-left"
              data-testid={`demo-cat-toggle-${g.category}`}>
              <ChevronRight className={`w-4 h-4 text-slate-400 shrink-0 transition-transform ${open ? 'rotate-90' : ''}`} />
              <div className="min-w-[200px]">
                <p className="text-[14px] font-bold text-slate-900">{g.category}</p>
                <p className="text-[10.5px] text-slate-500">
                  {t.people} {by}{t.people === 1 ? '' : 's'} · {share(t.demos)}% of demos · {g.courses.slice(0, 3).join(', ')}
                  {g.courses.length > 3 ? ` +${g.courses.length - 3}` : ''}
                </p>
              </div>
              {[['Demos', t.demos], ['Admissions', t.admissions], ['Conversion', pct(t.conversion_pct)],
                ['Not conducted', t.not_conducted], ['Value excl. GST', inr(t.value_ex_gst)],
                ['Feedback missing', t.fb_missing]].map(([l, v]) => (
                <div key={l} className="min-w-[88px]">
                  <p className="text-[9.5px] uppercase tracking-wider text-slate-400">{l}</p>
                  <p className="text-[13px] font-semibold text-slate-800 tabular-nums">{v}</p>
                </div>
              ))}
            </button>

            {open && (
              <div className="overflow-x-auto border-t border-slate-200">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-slate-50/80 text-[9.5px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                      <th className="px-4 py-2 text-left">{by === 'trainer' ? 'Trainer' : 'Counsellor'}</th>
                      <th className="px-3 py-2 text-right">Demos</th>
                      <th className="px-3 py-2 text-right">Admissions</th>
                      <th className="px-3 py-2 text-right">Conv %</th>
                      <th className="px-3 py-2 text-right">No Adm.</th>
                      <th className="px-3 py-2 text-right">Follow-up</th>
                      <th className="px-3 py-2 text-right">Pending</th>
                      <th className="px-3 py-2 text-right">Not Conducted</th>
                      <th className="px-3 py-2 text-right">Value excl. GST</th>
                      <th className="px-3 py-2 text-right">Awaiting Invoice</th>
                      <th className="px-3 py-2 text-right">FB Missing</th>
                    </tr>
                  </thead>
                  <tbody>
                    {g.rows.map((r) => (
                      <tr key={r.name} className="border-b border-slate-100 last:border-0 hover:bg-violet-50/60" data-testid={`demo-cat-row-${g.category}-${r.name}`}>
                        <td className="px-4 py-2.5 font-semibold text-slate-800">{r.name}</td>
                        <td className="px-3 py-2.5 text-right tabular-nums font-semibold text-slate-900">{r.demos}</td>
                        <td className="px-3 py-2.5 text-right tabular-nums text-emerald-700 font-semibold">{r.admissions}</td>
                        <td className="px-3 py-2.5 text-right tabular-nums text-violet-700">{pct(r.conversion_pct)}</td>
                        <td className="px-3 py-2.5 text-right tabular-nums text-slate-600">{r.no_admission || '—'}</td>
                        <td className="px-3 py-2.5 text-right tabular-nums text-slate-600">{r.follow_up || '—'}</td>
                        <td className="px-3 py-2.5 text-right tabular-nums text-slate-600">{r.pending || '—'}</td>
                        <td className={`px-3 py-2.5 text-right tabular-nums ${r.not_conducted ? 'text-rose-700 font-semibold' : 'text-slate-400'}`}>{r.not_conducted || '—'}</td>
                        <td className="px-3 py-2.5 text-right tabular-nums text-slate-900">{inr(r.value_ex_gst)}</td>
                        <td className={`px-3 py-2.5 text-right tabular-nums ${r.awaiting_invoice ? 'text-amber-700' : 'text-slate-400'}`}>{r.awaiting_invoice || '—'}</td>
                        <td className={`px-3 py-2.5 text-right tabular-nums ${r.fb_missing ? 'text-amber-700 font-semibold' : 'text-slate-400'}`}>{r.fb_missing || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        );
      })}
    </div>
  );
}
